import React, { useState } from 'react';
import './astyles/volunteerTable.css';

interface Volunteer {
  id: number;
  name?: string;
  firstname?: string;
  surname?: string;
  email: string;
  phone?: string;
  location?: string;
  skills?: string[] | null;
  availability?: string;
  application_status?: string;
  created_at?: string;
  status_updated_at?: string;
}

interface VolunteerTableProps {
  volunteers: Volunteer[];
}

function VolunteerTable({ volunteers }: VolunteerTableProps) {
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle loading state
  if (!volunteers) {
    return <div>Loading volunteers...</div>;
  }

  // Handle empty state
  if (volunteers.length === 0) {
    return <div>No volunteers found.</div>;
  }

  // Safe name display function
  const displayName = (volunteer: Volunteer): string => {
    if (volunteer.name) {
      return volunteer.name;
    }
    if (volunteer.firstname || volunteer.surname) {
      return `${volunteer.firstname || ''} ${volunteer.surname || ''}`.trim();
    }
    return 'Unknown';
  };

  // Format date safely
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Format application status
  const formatStatus = (status?: string): string => {
    if (!status) return 'pending';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Format skills for display
  const displaySkills = (skills?: string[] | null): string => {
    if (!skills || skills.length === 0) return 'No skills listed';
    return skills.join(', ');
  };

  // Open modal with volunteer details
  const handleViewDetails = (volunteer: Volunteer) => {
    setSelectedVolunteer(volunteer);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVolunteer(null);
  };

  // Handle status updates
  async function handleStatusUpdate(volunteerId: number, status: 'approved' | 'rejected') {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';
      const response = await fetch(`${API_BASE_URL}/api/applications/${volunteerId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Admin user
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.statusText}`);
      }

      // Close modal 
      handleCloseModal();
      window.location.reload();
    } catch (error) {
      console.error('Error updating volunteer status:', error);
      alert('Failed to update volunteer status. Please try again.');
    }
  }

  return (
    <>
      <div className="volunteer-table-container">
        <table className="volunteer-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th className="email-column">Email</th>
              <th className="skills-column">Skills</th>
              <th className="status-column">Status</th>
              <th className="date-column">Applied Date</th>
              <th>Actions</th>
             </tr>
          </thead>
          <tbody>
            {volunteers.map((volunteer) => (
              <tr key={volunteer.id}>
                 <td>{volunteer.id}</td>
                 <td>{displayName(volunteer)}</td>
                 <td className="email-column">{volunteer.email || 'N/A'}</td>
                 <td className="skills-column">
             <span className="skills-trim" title={displaySkills(volunteer.skills)}>
                {displaySkills(volunteer.skills)}
             </span>
              </td>
              <td className="status-column">
              <span className={`status-badge status-${volunteer.application_status || 'pending'}`}>
                {formatStatus(volunteer.application_status)}
               </span>
              </td>
            <td className="date-column">{formatDate(volunteer.created_at)}</td>
               <td>
                  <div className="action-buttons">
                    <button 
                    className="view-btn"
                    onClick={() => handleViewDetails(volunteer)}
                        >
                        View
                     </button>
                   </div>
               </td>
            </tr>
         ))}
          </tbody>
        </table>
      </div>

      {/* Volunteer Detail Modal */}
      {isModalOpen && selectedVolunteer && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Volunteer Application Review</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="volunteer-profile">
              <div className="profile-section">
                <h4>Personal Information</h4>
                <div className="profile-grid">
                  <div className="profile-item">
                    <label>Name:</label>
                    <span>{displayName(selectedVolunteer)}</span>
                  </div>
                  <div className="profile-item">
                    <label>Email:</label>
                    <span>{selectedVolunteer.email || 'N/A'}</span>
                  </div>
                  <div className="profile-item">
                    <label>Phone:</label>
                    <span>{selectedVolunteer.phone || 'N/A'}</span>
                  </div>
                  <div className="profile-item">
                    <label>Location:</label>
                    <span>{selectedVolunteer.location || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h4>Skills & Availability</h4>
                <div className="profile-grid">
                  <div className="profile-item full-width">
                    <label>Skills:</label>
                    <span>{selectedVolunteer.skills && selectedVolunteer.skills.length > 0 
                      ? selectedVolunteer.skills.join(', ') 
                      : 'No skills listed'}</span>
                  </div>
                  <div className="profile-item">
                    <label>Availability:</label>
                    <span>{selectedVolunteer.availability || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h4>Application Details</h4>
                <div className="profile-grid">
                  <div className="profile-item">
                    <label>Status:</label>
                    <span className={`status-badge status-${selectedVolunteer.application_status || 'pending'}`}>
                      {formatStatus(selectedVolunteer.application_status)}
                    </span>
                  </div>
                  <div className="profile-item">
                    <label>Applied:</label>
                    <span>{formatDate(selectedVolunteer.created_at)}</span>
                  </div>
                  <div className="profile-item">
                    <label>Last Updated:</label>
                    <span>{formatDate(selectedVolunteer.status_updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            {selectedVolunteer.application_status === 'pending' && (
              <div className="modal-actions">
                <button 
                  className="reject-btn"
                  onClick={() => handleStatusUpdate(selectedVolunteer.id, 'rejected')}
                >
                  Reject
                </button>
                <button 
                  className="approve-btn"
                  onClick={() => handleStatusUpdate(selectedVolunteer.id, 'approved')}
                >
                  Approve
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VolunteerTable;